import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ArrowDown } from 'lucide-react';
import {
  createInputTransaction,
  createInventoryItem,
} from '../../../redux/api';
import DeviceCategorySelect from '../../inventory/DeviceCategorySelect';
import SupplierSelect from '../../inventory/SupplierSelect';
import AssetCategoryFields, { resolveAssetCategory, showAssetCategoryConflict } from '../../inventory/AssetCategoryFields';
import { su } from '../../inventory/stockUi';
import NumberField, { parseNumber } from '../../inventory/NumberField';

const emptyForm = (stockKind) => ({
  itemCode: '',
  name: '',
  category: '',
  newCategory: '',
  deviceCategoryId: '',
  unit: 'kom',
  currentQuantity: '',
  purchasePrice: '',
  sellingPrice: '',
  minQuantity: '',
  supplierId: '',
  supplierName: '',
  notes: '',
  stockKind
});

const InventoryInputsView = ({ stockKind = 'WAREHOUSE' }) => {
  const { t } = useTranslation();
  const isAsset = stockKind === 'ASSET';
  const today = () => new Date().toISOString().split('T')[0];
  const [formData, setFormData] = useState(emptyForm(stockKind));

  useEffect(() => {
    setFormData(emptyForm(stockKind));
  }, [stockKind]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => setFormData(emptyForm(stockKind));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let category = formData.category;
      if (isAsset) {
        try {
          category = await resolveAssetCategory({
            selectedName: formData.category,
            newName: formData.newCategory,
          });
        } catch (categoryError) {
          if (categoryError.code === 'BOTH') {
            showAssetCategoryConflict();
            return;
          }
          throw categoryError;
        }
      }
      const quantity = parseNumber(formData.currentQuantity);
      const purchasePrice = parseNumber(formData.purchasePrice);
      const created = await createInventoryItem({
        itemCode: formData.itemCode,
        name: formData.name,
        category,
        deviceCategoryId: isAsset ? undefined : formData.deviceCategoryId,
        unit: formData.unit || 'kom',
        purchasePrice,
        sellingPrice: parseNumber(formData.sellingPrice),
        minQuantity: parseNumber(formData.minQuantity),
        currentQuantity: 0,
        supplierId: formData.supplierId || undefined,
        supplierName: formData.supplierName,
        notes: formData.notes,
        stockKind
      });
      const item = created?.data || created;
      await createInputTransaction({
        itemId: item._id,
        quantity,
        unitPrice: purchasePrice,
        documentNumber: `${isAsset ? 'INV' : 'MAG'}-${formData.itemCode || Date.now()}`,
        documentDate: today(),
        partnerName: formData.supplierName,
        notes: formData.notes,
        stockKind
      });
      toast.success(isAsset ? 'Sredstvo je dodato u inventar' : 'Artikal je dodat u magacin');
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Greška pri čuvanju ulaza');
    }
  };

  return (
    <div className={su.page}>
      <div className={su.wrap}>
        <div className={`${su.card} ${su.cardPad}`}>
          <h1 className={su.title}>
            <ArrowDown className={su.icon} />
            {isAsset
              ? t('inventory.assetInputs.title', 'Ulaz sredstava')
              : t('inventory.inputs.title', 'Ulaz robe')}
          </h1>
          <p className={su.subtitle}>
            {isAsset
              ? t('inventory.assetInputs.subtitle', 'Dodavanje alata, opreme i ostalih sredstava')
              : t('inventory.inputs.subtitle', 'Dodavanje novih delova u magacin')}
          </p>
        </div>

        <div className={`${su.card} ${su.cardPad}`}>
          <form onSubmit={handleSubmit} className="space-y-2">
            <div className={su.grid}>
              <div>
                <label className={su.label}>{t('inventory.itemCode', 'Šifra artikla')} *</label>
                <input type="text" name="itemCode" value={formData.itemCode} onChange={handleInputChange} required className={su.input} />
              </div>
              <div>
                <label className={su.label}>{t('inventory.name', 'Naziv')} *</label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} required className={su.input} />
              </div>
              {isAsset ? (
                <AssetCategoryFields
                  selectedName={formData.category}
                  newName={formData.newCategory}
                  onChange={({ selectedName, newName }) => setFormData((prev) => ({
                    ...prev,
                    category: selectedName,
                    newCategory: newName,
                  }))}
                />
              ) : (
              <div>
                <label className={su.label}>{t('inventory.category', 'Kategorija')}</label>
                <DeviceCategorySelect
                  valueId={formData.deviceCategoryId}
                  onChange={({ id, name }) => setFormData((prev) => ({
                    ...prev,
                    deviceCategoryId: id,
                    category: name
                  }))}
                />
              </div>
              )}
              <div>
                <label className={su.label}>{t('inventory.quantity', 'Količina')} *</label>
                <NumberField name="currentQuantity" value={formData.currentQuantity} onChange={handleInputChange} step="0.01" min="0" required />
              </div>
              <div>
                <label className={su.label}>{t('inventory.purchasePrice', 'Nabavna cena')} *</label>
                <NumberField name="purchasePrice" value={formData.purchasePrice} onChange={handleInputChange} step="0.01" min="0" required />
              </div>
              {!isAsset && (
                <div>
                  <label className={su.label}>{t('inventory.sellingPrice', 'Prodajna cena')}</label>
                  <NumberField name="sellingPrice" value={formData.sellingPrice} onChange={handleInputChange} step="0.01" min="0" />
                </div>
              )}
              <div>
                <label className={su.label}>{t('inventory.minQuantity', 'Min. količina za obaveštenje')}</label>
                <NumberField name="minQuantity" value={formData.minQuantity} onChange={handleInputChange} min="0" />
              </div>
              <div>
                <label className={su.label}>{t('inventory.inputs.partnerName', 'Dobavljač')}</label>
                <SupplierSelect
                  valueId={formData.supplierId}
                  valueName={formData.supplierName}
                  onChange={({ id, name }) => setFormData((prev) => ({
                    ...prev,
                    supplierId: id,
                    supplierName: name
                  }))}
                />
              </div>
            </div>
            <div>
              <label className={su.label}>{t('common.notes', 'Napomene')}</label>
              <textarea name="notes" value={formData.notes} onChange={handleInputChange} rows="2" className={su.textarea} />
            </div>
            <div className="flex justify-end gap-1.5">
              <button type="button" onClick={resetForm} className={su.btnGhost}>
                {t('common.cancel', 'Otkaži')}
              </button>
              <button type="submit" className={su.btnPrimary}>
                {t('common.save', 'Sačuvaj')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InventoryInputsView;
